import L from 'leaflet';

/**
 * Compute the axis-aligned bounding box of a set of [lat, lng] coordinate
 * pairs (CRS.Simple pixel coords: lat = Y, lng = X).
 *
 * Returns `[[south, west], [north, east]]` — i.e. `[[minLat, minLng],
 * [maxLat, maxLng]]` — ready to hand to Leaflet's `fitBounds`, or `null`
 * when the input is empty (caller keeps its fallback view). A single
 * coordinate yields degenerate (zero-area) bounds, which `fitBounds`
 * handles via its `maxZoom` clamp.
 */
export function coordinatesBounds(
  coordinates: ReadonlyArray<readonly [number, number]>,
): [[number, number], [number, number]] | null {
  const first = coordinates[0];
  if (!first) return null;
  let minLat = first[0];
  let maxLat = first[0];
  let minLng = first[1];
  let maxLng = first[1];
  for (const [lat, lng] of coordinates) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

/**
 * Project a latLng to its destination point during a zoom animation.
 *
 * Uses Leaflet's internal `_latLngToNewLayerPoint` (underscore prefix → not
 * part of the documented public API). There is no public equivalent for
 * projecting to an *in-flight* zoom target; without it, markers can't glide
 * in sync with the tile layer during `zoomanim`.
 *
 * Pinned via `package.json` to ~1.9.x. Verify on every Leaflet bump. If the
 * API ever disappears (e.g. Leaflet 2.x), we silently fall back to the
 * current layer point — markers will pop into place at the end of zoom
 * rather than glide, but the app continues to function.
 *
 * Callers should treat this as a single, well-known leak of Leaflet
 * internals. Do not introduce new private-API usages elsewhere.
 */
export function projectToNewLayerPoint(
  map: L.Map,
  latLng: L.LatLng,
  targetZoom: number,
  targetCenter: L.LatLng,
): L.Point {
  const internal = map as unknown as {
    _latLngToNewLayerPoint?: (ll: L.LatLng, z: number, c: L.LatLng) => L.Point;
  };
  if (typeof internal._latLngToNewLayerPoint === 'function') {
    return internal._latLngToNewLayerPoint(latLng, targetZoom, targetCenter);
  }
  if (import.meta.env.DEV) {
    console.warn(
      '[map/coords] Leaflet `_latLngToNewLayerPoint` missing; ' +
        'falling back to `latLngToLayerPoint`. Markers will not glide ' +
        'during zoom. Audit Leaflet version.',
    );
  }
  return map.latLngToLayerPoint(latLng);
}
